/**
 * IGA Maturity Assessment — Result page.
 * Fetches /api/v1/assessments/submissions/{id}/result and renders:
 *  - Score gauge
 *  - Radar chart with 10 domain scores
 *  - Bar list per domain
 *  - Top recommendations
 *  - Project complexity card
 *  - CTAs to talk to specialist
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import MethodologyCitations from '@/components/assessment/MethodologyCitations'
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from 'recharts'

interface MaturityBand { id: string; label: string; color: string; description: string; tone: string; min: number; max: number }
interface DomainScore {
  domain_id: number; slug: string; name: string; color: string; score: number; max_score: number
  percentage: number; band_id: string; band_label: string; band_color: string
}
interface Recommendation {
  priority: 'alta' | 'media' | 'baixa'
  domain_id: number; domain_name: string; title: string; description: string
  effort: string; impact: string; current_score: number; current_band: string
  control_number?: string
  control_name?: string
  control_priority?: 'critical' | 'high' | 'medium' | 'mature' | 'na'
  matrix_level?: number
  matrix_level_tag?: string
}
interface Result {
  submission_id: string; assessment_slug: string; pillar: string; submitted_at: string
  company_name: string; contact_name: string
  total_score: number; total_max: number; total_percentage: number
  maturity_band: MaturityBand
  domain_scores: DomainScore[]
  critical_domains: DomainScore[]
  strength_domains: DomainScore[]
  recommendations: Recommendation[]
  project_complexity: { level: string; label: string; description: string }
  sector_citations?: string[]
  norm_citations?: string[]
}

export type ResultProps = {
  eyebrow?: string
  title?: string
  maxScore?: number
  frameworkLabel?: string
  footerLabel?: string
}

export default function AssessmentIGAResultPage({
  eyebrow = 'Nai Compliance Center · IGA',
  title = 'IGA Maturity Assessment',
  maxScore = 500,
  frameworkLabel = 'do framework IGA',
  footerLabel = 'IGA Maturity Assessment',
}: ResultProps = {}) {
  const { id } = useParams()
  const [result, setResult] = useState<Result | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    fetch(`/api/v1/assessments/submissions/${id}/result`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setResult)
      .catch(e => setErr(String(e?.message || e)))
  }, [id])

  if (err) return <Center title="Erro ao carregar resultado" sub={err} />
  if (!result) return <Center title="Carregando seu resultado…" sub="" />

  const radarData = result.domain_scores.map(d => ({
    domain: d.name.split(' ')[0],
    fullName: d.name,
    score: d.percentage,
  }))

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#050912] via-[#0a131f] to-[#050912] text-slate-100">
      {/* Print styles — inline so they apply only when window.print() is invoked */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          html, body { background: #ffffff !important; color: #1a1a1a !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          *, *::before, *::after { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          header.sticky { position: static !important; background: #00284a !important; padding: 18px 0 !important; }
          header.sticky * { color: #ffffff !important; }
          header.sticky .text-cyan-400 { color: #06b6d4 !important; }
          .min-h-screen { min-height: 0 !important; background: #ffffff !important; }
          main { max-width: 100% !important; padding: 0 !important; }
          section { background: #ffffff !important; border: 1px solid #e2e8f0 !important; color: #1a1a1a !important; box-shadow: none !important; page-break-inside: avoid; }
          section h1, section h2, section h3, section h4 { color: #1a1a1a !important; }
          section p, section li, section span, section td { color: #1f2937 !important; }
          .text-white { color: #1a1a1a !important; }
          .text-slate-100, .text-slate-200, .text-slate-300, .text-slate-400 { color: #4b5563 !important; }
          .text-slate-500 { color: #6b7280 !important; }
          .bg-slate-900\\/60, .bg-slate-800\\/40, .bg-slate-800\\/60 { background: #f9fafb !important; }
          .from-cyan-700\\/40, .to-emerald-700\\/40 { background: #ecfeff !important; }
          .border-cyan-500\\/40, .border-cyan-800\\/40 { border-color: #06b6d4 !important; }
          footer { background: #ffffff !important; color: #6b7280 !important; border-color: #e5e7eb !important; }
        }
        .print-only { display: none; }
      `}</style>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#00284a]/85 border-b border-slate-800 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <img src="https://nai-it.com.br/wp-content/uploads/2025/11/logo_nai_branco.png"
               alt="NAI" className="h-9 w-auto" />
          <div className="border-l border-slate-700 pl-3 flex-1">
            <div className="text-xs font-bold uppercase tracking-widest text-cyan-400">
              {eyebrow}
            </div>
            <div className="text-sm font-semibold text-white">
              Resultado · {result.company_name}
            </div>
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            className="no-print inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold transition"
            title="Imprimir ou salvar como PDF"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            <span className="hidden sm:inline">Baixar PDF</span>
            <span className="sm:hidden">PDF</span>
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Top: Gauge + Band */}
        <section className="grid lg:grid-cols-[auto_1fr] gap-6 lg:gap-10 items-center bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8">
          <ScoreGauge score={result.total_score} max={result.total_max}
                      pct={result.total_percentage} color={result.maturity_band.color} />
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-cyan-400">
              Seu nível de maturidade
            </div>
            <h1 className="mt-2 text-3xl sm:text-5xl font-black tracking-tight" style={{ color: result.maturity_band.color }}>
              {result.maturity_band.label}
            </h1>
            <p className="mt-3 text-slate-300 text-base leading-relaxed">
              {result.maturity_band.description}
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-xs text-slate-400 font-mono">
              Score: <span className="font-bold text-white">{result.total_score}</span> de {result.total_max} ·
              {result.total_percentage}%
            </div>
          </div>
        </section>

        {/* Radar Chart */}
        <section className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8">
          <h2 className="text-sm font-bold uppercase tracking-widest text-cyan-400 mb-1">
            Mapa de Maturidade · {result.domain_scores.length} Domínios
          </h2>
          <p className="text-sm text-slate-400 mb-6">
            Visão consolidada do seu desempenho em cada domínio {frameworkLabel}.
          </p>
          <div className="w-full h-[380px] sm:h-[460px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="#1e2d4a" />
                <PolarAngleAxis dataKey="domain" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#475569', fontSize: 10 }} />
                <Radar name="Score" dataKey="score" stroke="#06b6d4"
                       fill="#06b6d4" fillOpacity={0.45} strokeWidth={2.5}
                       isAnimationActive={false} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Domain bars */}
        <section className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8">
          <h2 className="text-sm font-bold uppercase tracking-widest text-cyan-400 mb-1">
            Pontuação por domínio
          </h2>
          <p className="text-sm text-slate-400 mb-6">
            Cada domínio vale até 50 pontos · barras coloridas pela faixa de maturidade.
          </p>
          <div className="space-y-3">
            {result.domain_scores.map(d => (
              <div key={d.domain_id}>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="font-semibold text-slate-200">
                    {String(d.domain_id).padStart(2, '0')}. {d.name}
                  </span>
                  <span className="font-mono" style={{ color: d.band_color }}>
                    {d.score}/{d.max_score} · {d.band_label}
                  </span>
                </div>
                <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700"
                       style={{ width: `${d.percentage}%`, background: d.band_color }} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Recommendations */}
        {result.recommendations.length > 0 && (
          <section className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8">
            <h2 className="text-sm font-bold uppercase tracking-widest text-cyan-400 mb-1">
              Recomendações priorizadas
            </h2>
            <p className="text-sm text-slate-400 mb-6">
              Roadmap inicial com base nos gaps mais críticos. Ordenado por prioridade × impacto.
            </p>
            <div className="space-y-3">
              {result.recommendations.map((r, i) => <RecCard key={i} rec={r} />)}
            </div>
          </section>
        )}

        {/* Complexity */}
        <section className="bg-gradient-to-br from-slate-900/60 to-cyan-900/20 border border-cyan-800/40 rounded-2xl p-6 sm:p-8">
          <h2 className="text-sm font-bold uppercase tracking-widest text-cyan-400 mb-3">
            Estimativa de complexidade do projeto
          </h2>
          <div className="flex items-baseline gap-4 flex-wrap">
            <span className="text-3xl sm:text-5xl font-black text-white tracking-tight">
              {result.project_complexity.label}
            </span>
            <span className="text-sm text-slate-400">{result.project_complexity.description}</span>
          </div>
        </section>

        {/* CTA */}
        <section className="no-print bg-gradient-to-r from-cyan-700/40 to-emerald-700/40 border border-cyan-500/40 rounded-2xl p-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-3">
            Quer discutir esses números com nosso time?
          </h2>
          <p className="text-slate-200 mb-6 max-w-xl mx-auto">
            Em 30 minutos detalhamos sua nota domínio a domínio e desenhamos o roadmap
            de adequação ao seu cenário (legados, terceiros, compliance, orçamento).
          </p>
          <a
            href={`https://wa.me/5511996344065?text=${encodeURIComponent(`Olá! Acabei de concluir o ${title} (score ${result.total_score}/${maxScore} — ${result.maturity_band.label}). Gostaria de conversar sobre o roadmap.`)}`}
            target="_blank" rel="noopener"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-xl transition"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
            Falar com especialista no WhatsApp
          </a>
          <p className="text-xs text-slate-400 mt-4">
            Material confidencial · sem compromisso · resposta em até 1h útil
          </p>
        </section>

        {/* Fundamentação metodológica + citações setoriais (A5) */}
        {(result.norm_citations?.length || result.sector_citations?.length) && (
          <section className="mt-8">
            <MethodologyCitations
              norms={result.norm_citations || []}
              sectorCitations={result.sector_citations || []}
              disclaimer="Este é um self-assessment indicativo. Avaliações formais por terceiros tendem a corrigir uma superestimativa típica de 10 a 20%."
            />
          </section>
        )}

        {/* Share / metadata */}
        <section className="text-center text-xs text-slate-500 py-4">
          Submission ID: <span className="font-mono text-slate-400">{result.submission_id}</span>
          <br />
          Salve esta página · você pode acessá-la novamente a qualquer momento.
        </section>
      </main>

      <footer className="py-6 border-t border-slate-800 text-center text-[11px] text-slate-500">
        © NAI · {footerLabel} · nai-it.com
      </footer>
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────
function ScoreGauge({ score, max, pct, color }: { score: number; max: number; pct: number; color: string }) {
  const size = 200
  const r = 86
  const circ = 2 * Math.PI * r
  const offset = circ - (pct / 100) * circ
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,.06)" strokeWidth="14" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth="14" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 1.4s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-black tracking-tight" style={{ color }}>{score}</span>
        <span className="text-xs text-slate-500">de {max}</span>
        <span className="text-[10px] text-slate-400 font-mono mt-1">{pct}%</span>
      </div>
    </div>
  )
}

function RecCard({ rec }: { rec: Recommendation }) {
  const ctrlPriColor =
    rec.control_priority === 'critical' ? '#ef4444' :
    rec.control_priority === 'high' ? '#f97316' :
    rec.control_priority === 'medium' ? '#eab308' :
    rec.priority === 'alta' ? '#ef4444' :
    rec.priority === 'media' ? '#f59e0b' : '#3b82f6'
  const impColor =
    rec.impact === 'alto' ? '#22c55e' :
    rec.impact === 'medio' ? '#3b82f6' : '#64748b'
  const ctrlPriLabel =
    rec.control_priority === 'critical' ? 'Crítico' :
    rec.control_priority === 'high' ? 'Alto' :
    rec.control_priority === 'medium' ? 'Médio' : rec.priority
  return (
    <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-5 hover:bg-slate-800/60 transition">
      <div className="flex items-start gap-3">
        <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
              style={{ background: ctrlPriColor + '22', color: ctrlPriColor, border: `1px solid ${ctrlPriColor}55` }}>
          {ctrlPriLabel}
        </span>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm sm:text-base font-bold text-white leading-snug">{rec.title}</h4>
          <p className="text-xs sm:text-sm text-slate-400 mt-1.5 leading-relaxed">{rec.description}</p>
          <div className="flex flex-wrap gap-2 mt-3 text-[10px]">
            {rec.matrix_level_tag && (
              <span className="px-2 py-0.5 rounded bg-cyan-900/40 text-cyan-300 border border-cyan-800/60 font-semibold">
                {rec.matrix_level_tag}
              </span>
            )}
            <span className="px-2 py-0.5 rounded bg-slate-700/60 text-slate-300">
              {rec.domain_name}
            </span>
            <span className="px-2 py-0.5 rounded font-semibold"
                  style={{ background: impColor + '22', color: impColor }}>
              impacto {rec.impact}
            </span>
            <span className="px-2 py-0.5 rounded bg-slate-700/40 text-slate-400">
              {rec.effort.replace('_', ' ')}
            </span>
            <span className="px-2 py-0.5 rounded bg-slate-700/40 text-slate-400">
              score atual: {rec.current_score}/50 · {rec.current_band}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

function Center({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="min-h-screen bg-[#050912] flex items-center justify-center text-center px-4">
      <div>
        <div className="text-cyan-400 text-sm font-bold uppercase tracking-widest mb-3">Nai Compliance Center</div>
        <h1 className="text-2xl font-black text-white">{title}</h1>
        {sub && <p className="text-sm text-slate-400 mt-2 max-w-md">{sub}</p>}
      </div>
    </div>
  )
}
